import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Languages, PenLine, Wand2 } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import heroImg from "@/assets/ai-tools-hero.webp";
import contextImg from "@/assets/ai-tools-context.webp";
import configImg from "@/assets/ai-tools-config.webp";

const IMG: Record<string, string> = { hero: heroImg, context: contextImg, config: configImg };

export const Route = createFileRoute("/platform/ai-tools")({
  head: () => ({
    meta: [
      { title: "AI Tools — banana.cards" },
      { name: "description", content: "Write, summarize, translate and score with AI — wherever you work in Banana." },
      { property: "og:title", content: "AI Tools — banana.cards" },
      { property: "og:description", content: "Write, summarize, translate and score with AI — wherever you work in Banana." },
      { property: "og:image", content: IMG['hero'] },
    ],
    links: alternateLinkList("/platform/ai-tools", "en"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="AI Tools"
      title="A toolbox of AI,"
      italic="built into your work."
      description="Write, summarize, translate, score and suggest next actions. Banana's AI tools live exactly where you work — your inbox, your CRM, your phone."
      heroImage={IMG['hero']}
      heroImageAlt="In-context AI tools panel inside a banana.cards contact view drafting a personalized follow-up email"
      metrics={[{ v: "12+", l: "AI tools" }, { v: "50+", l: "languages" }, { v: "1-click", l: "actions" }]}
      bullets={[
        { icon: PenLine, title: "AI writer", desc: "Draft emails, proposals, follow-ups and notes — in your tone." },
        { icon: Languages, title: "AI translator", desc: "Speak to clients in their language — no copy-pasting into other tools." },
        { icon: Wand2, title: "Next-best action", desc: "AI suggests who to contact next, and what to say, based on real context." }
      ]}
      deepDives={[
        { eyebrow: "In context", title: "Right there in the", italic: "contact view.", description: "Every AI tool is one tap from the contact, the deal or the inbox — no switching apps, no copy-paste.", imageAlt: "Contact detail view on banana.cards with one-tap AI actions to summarize, draft replies and score deals", image: IMG["context"], reverse: false, points: ["Summarize emails and meetings", "Draft replies with context", "Score deals automatically", "Generate proposals from contacts"] },
        { eyebrow: "Configurable", title: "Choose your model,", italic: "control the cost.", description: "Pick the model behind each AI tool from a central config — Banana tracks usage and never hard-codes a vendor.", imageAlt: "Central AI model registry on banana.cards choosing which LLM powers each feature with per-feature cost limits", image: IMG["config"], reverse: true, points: ["Central model registry", "Per-feature model selection", "Usage logging & cost limits", "BYO API key for enterprise"] }
      ]}
      howItWorks={[
        { title: "Pick a tool", desc: "Writer, translator, summarizer, scorer." },
        { title: "One-tap from context", desc: "Banana pulls the data automatically." },
        { title: "Review & edit", desc: "You stay in control of every output." },
        { title: "Track usage", desc: "See cost and impact per tool." }
      ]}
      capabilities={[
        "AI email writer & rewriter",
        "AI meeting summarizer",
        "AI translator (50+ languages)",
        "AI lead scorer",
        "AI deal next-step suggester",
        "AI voice-note transcriber",
        "AI proposal generator",
        "Central model configuration"
      ]}
      faq={[
        { q: "Which AI tools are included in Banana?", a: "Business Card Scanner (photo → contact), Meeting Notetaker (transcript + summary + action items), Email Drafter (in-your-voice replies and follow-ups), Contact Enrichment (title, company, socials), Scout (natural-language prospecting on Google Places), Contract Drafter (templates and clause suggestions), Smart Tagging and Segmentation, Auto-translate for cards and messages, and Next-Best-Action suggestions on contacts and deals." },
        { q: "Can I bring my own API key (BYOK)?", a: "Yes — on Enterprise plans you can plug your own OpenAI, Anthropic or Google AI key into any tool. Usage is billed directly to your provider and Banana acts as the routing layer, so you keep full cost control and data residency." },
        { q: "Can I see what each AI tool is costing me?", a: "Yes — every AI invocation is logged in the AI Usage dashboard with the tool name, the model used, token counts (input/output), latency and the cost per call. Filter by user, by team or by feature to spot heavy consumers." },
        { q: "Can admins cap AI usage to control spend?", a: "Yes — set monthly token or cost caps per user, per team or per feature. When a cap is hit, that user (or that feature) is paused with a clear message, while the rest of the workspace keeps running." },
        { q: "Are AI tools optional?", a: "Yes — every AI feature can be toggled per workspace and per teammate. Privacy-sensitive teams can turn off external AI tools entirely and keep using Banana as a pure CRM." },
        { q: "Do the AI tools work in my language?", a: "Yes — drafting, transcription, summarization and enrichment work in 50+ languages, with quality close to native English in the major European, East-Asian and Southeast-Asian languages." }
      ]}
      outro={ { title: "AI that gets out", italic: "of your way.", description: "Tools that show up exactly where you need them — and never elsewhere." } }
    />
  );
}
